import { Card } from './card';
import { EvseOperationalStatus } from './evse-operational-status';

export type EvseController = {
  configuration?: EvseControllerConfigurationItem[];
  configurationFetchedAt?: Date;
  tariffProfileId?: string;
  optOutOfScanToPay?: boolean;
  name?: string;
  smartCharging?: {
    method?: string;
    paired?: boolean;
    pairingCode?: string;
  };
  setupProgress?: {
    state?: string;
    currentStep?: number;
  };
  associatedAccounts?: {
    readAccess?: any[];
    writeAccess?: any[];
  };
  accessGroupIds?: string[];
  startCostInvoiceIds?: string[];
  numConnectors?: number;
  enablePublicFreeCharging?: boolean;
  enablePublicCharging?: boolean;
  disableRoamingChargeForProviders?: boolean;
  disableRoamingChargePartyIds?: string[];
  enableTariffProfileOverride?: boolean;
  isDisabled?: boolean;
  ocppAuthorizationMethod?: EvseControllerAuthenticationMethod;
  connectivityState?: EvseControllerConnectivityState;
  rebootRequired?: boolean;
  setupInProgress?: boolean;
  syncOcpiCredentialIds?: string[];
  ocppIdentity?: string;
  ocppFtpPassword?: string;
  ocppAuthorizationKey?: string;
  providerId?: string;
  numericIdentity?: number;
  createdAt?: string;
  accessGroupIdsHistory?: any[];
  connectors?: EvseControllerConnector[];
  authorizationInfo?: EvseAuthenticationAttempt;
  connectorsHistory?: {
    _id?: string;
    connectorObjectId?: string;
    createdAt?: string;
    id?: string;
  }[];
  costSettings?: EvseControllerCostSettings[];
  bootInfoSerialNumbers?: {
    _id?: string;
    serialNumber?: string;
    lastBootedAt?: string;
    id?: string;
  }[];
  externalSync?: any[];
  updatedAt?: string;
  connectivityStateUpdatedAt?: string;
  coordinatorConnectedAt?: string;
  coordinatorDisconnectedAt?: string;
  coordinatorStatus?: {
    state?: string;
    connectionInfo?: {
      remoteAddress?: string;
      protocol?: OCPPProtocolVersion;
      host?: string;
    };
    identifier?: string;
    websocketId?: number;
  };
  lastConnectivityMode?: string;
  bootInfo?: {
    chargePointVendor?: string;
    chargePointModel?: string;
    chargePointSerialNumber?: any;
    chargeBoxSerialNumber?: any;
    firmwareVersion?: string;
    iccid?: any;
    imsi?: any;
    meterType?: string;
    meterSerialNumber?: string;
    options?: {
      protocol?: string;
    };
  };
  heartbeatReceivedAt?: string;
  lastBootedAt?: string;
  serialNumber?: string;
  simpleSerialNumber?: string;
  tokenAuthorizeReceivedAt?: string;
  connectorStatus?: {
    [key: number]: ConnectorNode;
  };
  evseIdRef?: {
    spotOperatorId?: string;
    idType?: string;
    providerId?: string;
    powerOutletId?: string;
    createdAt?: string;
    updatedAt?: string;
    assignedAt?: string;
    evseControllerId?: string;
    countryCode?: string;
    identifier?: string;
    url?: string;
  };
  evseId?: string;
  locationId?: string;
  normalizedEvseId?: string;
  accountId?: string;
  billingPlanId?: string;
  maintenanceAccountId?: string;
  activatedAt?: string;
  timezone?: string;
  energyDeliveryArea?: string;
  ocppProtocolVersion?: OCPPProtocolVersion;
  id?: string;
  account?: {
    id?: string;
    name?: string;
    billing?: {
      sepaAccepted?: boolean;
      sepaAcceptedAt?: string;
    };
    contact?: {
      phoneCountryCode?: string;
      firstName?: string;
      lastName?: string;
      phoneNo?: string;
      email?: string;
    };
    associatedAccounts?: {
      readAccess?: any[];
      writeAccess?: any[];
    };
    paymentTerm?: number;
    platformFeatures?: any[];
    enableApiAccess?: boolean;
    notifyEVSEIssues?: boolean;
    notifyEVSEIssuesRecipient?: string;
    defaultLangCode?: string;
    vatNo?: any;
    type?: string;
    billingCurrency?: string;
    providerId?: string;
    createdAt?: string;
    updatedAt?: string;
    nonLegacy?: boolean;
    numericReferenceCode?: number;
  };
  provider?: {
    id?: string;
    supportedSmartChargingProviders?: string[];
    individualFees?: number;
    organisationFees?: number;
    isNewsletterFeatureEnabled?: boolean;
    activeCurrencies?: string[];
    name?: string;
    slug?: string;
    tosUrl?: string;
    companyInfo?: string;
    enableInvoicing?: boolean;
    enableSelfSignup?: boolean;
    enableMspSessionDeletion?: boolean;
    enableWebAnalytics?: boolean;
    supportedLanguages?: string[];
    enableChargeAtHome?: boolean;
    enableChargeStationPairChargeCard?: boolean;
    enabledChargeProducts?: string[];
    enableAccountTiers?: boolean;
    optOutOfScanToPay?: boolean;
    logoUrl?: string;
    invertedLogoUrl?: string;
    faviconUrl?: string;
    primaryColorHex?: string;
    primaryColorDarkHex?: string;
    address?: string;
    supportEmail?: string;
    supportPhoneNo?: string;
    supportUrl?: string;
    sandboxSettings?: {
      _id?: string;
      defaultDeveloperAccountId?: string;
      defaultDeveloperUserId?: string;
      cpoAccountId?: string;
      mspAccountId?: string;
    };
    supportedServices?: string[];
    supportedPaymentMethods?: string[];
  };
  maintenanceAccount?: {
    id?: string;
    name?: string;
    billing?: {
      sepaAccepted?: boolean;
      sepaAcceptedAt?: string;
    };
    contact?: {
      phoneCountryCode?: string;
      firstName?: string;
      lastName?: string;
      phoneNo?: string;
      email?: string;
    };
    associatedAccounts?: {
      readAccess?: any[];
      writeAccess?: any[];
    };
    paymentTerm?: number;
    platformFeatures?: any[];
    enableApiAccess?: boolean;
    notifyEVSEIssues?: boolean;
    notifyEVSEIssuesRecipient?: string;
    defaultLangCode?: string;
    vatNo?: any;
    type?: string;
    billingCurrency?: string;
    providerId?: string;
    createdAt?: string;
    updatedAt?: string;
    nonLegacy?: boolean;
    numericReferenceCode?: number;
  };
  billingPlan?: {
    id?: string;
    priority?: number;
    name?: string;
    details?: {
      [key: string]: {
        features?: string[];
        name?: string;
        descriptionLong?: string;
      };
    };
    providerContext?: string;
    type?: string;
    isPublic?: boolean;
    isPrimary?: boolean;
    tags?: string[];
    billingPeriod?: string;
    pricePerCard?: number;
    pricePerSession?: number;
    pricePerDevice?: number;
    pricePerPeriod?: number;
    providerId?: string;
    createdAt?: string;
    updatedAt?: string;
    prices?: {
      perPeriod?: number;
      perCard?: number;
      perDevice?: number;
      perSession?: number;
      _id?: string;
      currency?: string;
    }[];
  };
  location?: {
    id?: string;
    geoLocation?: {
      type?: string;
      coordinates?: number[];
    };
    associatedAccounts?: {
      readAccess?: any[];
      writeAccess?: any[];
    };
    providerContext?: string;
    accessPolicy?: AccessPolicy;
    status?: string;
    evseIds?: string[];
    hasTariffInfo?: boolean;
    connectorTypes?: string[];
    evses?: {
      uid?: string;
      evseControllerId?: string;
      ocppIdentity?: string;
      evse_id?: string;
      normalizedEvseId?: string;
      status?: string;
      isDisabled?: boolean;
      enablePublicCharging?: boolean;
      authorizationTypes?: string[];
      setupFinished?: boolean;
      connectors?: {
        uid?: string | null;
        id?: string | null;
        standard?: string | null;
        format?: string | null;
        power_type: string | null;
        voltage?: number | null;
        amperage?: number | null;
        last_updated?: string | null;
        evseId?: string;
      }[];
      deleted?: boolean;
      last_updated?: string;
    }[];
    facilities?: any[];
    syncOcpiCredentialIds?: string[];
    isPublished?: boolean;
    publishingMode?: string;
    accountId?: string;
    userId?: string;
    providerId?: string;
    source?: string;
    country?: string;
    name?: string;
    address?: string;
    postal_code?: string;
    city?: string;
    creditBillingTarget?: any[];
    externalSync?: {
      currentDirtyFlags?: string[];
      _id?: string;
      name?: string;
      states?: {
        endpoint?: string;
        status?: string;
        totalRuns?: number;
        _id?: string;
        nextTryAfter?: any;
      }[];
    }[];
    createdAt?: string;
    updatedAt?: string;
    numericIdentity?: number;
    last_updated?: string;
    billingPolicy?: {
      usageReimbursement?: {
        type?: string;
        percentage?: number;
        entity?: {
          type?: string;
          userId?: string;
        };
      }[];
      subscriptionBilling?: {
        percentage?: number;
        entity?: {
          type?: string;
          accountId?: string;
        };
      }[];
    };
    infraProviderId?: string;
    maxPower?: number;
    powerType?: string;
    energyDeliveryArea?: string;
  };
  pairedChargeCardId?: string;
  pairedChargeCardSync?: EvseControllerSyncMetadata<PairedChargeCardSyncStatus>;
  pairedChargeCard?: Card;
  accessGroups?: any[];
  ocppChargingStationId?: string;
  labels?: string[];
  isPairingChargeCardAllowed?: boolean;
  diagnosticsRequestedAt?: string;
  firmwareUpload?: {
    createdAt: string;
    filename: string;
    mimeType: string;
  };
  diagnosticsUpload?: {
    filename: string;
    rawUrl: string;
  };
  connectionType?: EvseControllerConnectionType;
  connectionProtocol?: EvseControllerConnectionProtocol;
  configureSecureConnectionSync?: EvseControllerSyncMetadata<ConfigureSecureConnectionSyncStatus>;
  renewBasicAuthPasswordSync?: EvseControllerSyncMetadata<RenewBasicAuthPasswordSyncStatus>;
  description?: string;
  evseOperationalStatusId: string;
  evseOperationalStatus?: EvseOperationalStatus;

  // This field should never be returned to the client because deleted charging stations should not be visible
  // but that's useful in some specific cases like when we need to show evse background jobs for a deleted evses
  deleted?: boolean;
};

export interface EvseControllerSyncMetadata<T> {
  executionId: string;
  status: T;
  statusDetails?: string;
}

export const SyncMetadataStatus = {
  InProgress: 'InProgress',
  Completed: 'Completed',
  Failed: 'Failed',
} as const;

export const RenewBasicAuthPasswordSyncStatus = {
  ...SyncMetadataStatus,
} as const;
export type RenewBasicAuthPasswordSyncStatus =
  (typeof RenewBasicAuthPasswordSyncStatus)[keyof typeof RenewBasicAuthPasswordSyncStatus];

export const PairedChargeCardSyncStatus = {
  ...SyncMetadataStatus,
} as const;
export type PairedChargeCardSyncStatus =
  (typeof PairedChargeCardSyncStatus)[keyof typeof PairedChargeCardSyncStatus];

export const ConfigureSecureConnectionSyncStatus = {
  ...SyncMetadataStatus,
} as const;
export type ConfigureSecureConnectionSyncStatus =
  (typeof ConfigureSecureConnectionSyncStatus)[keyof typeof ConfigureSecureConnectionSyncStatus];

export type EvseControllerCostSettings = {
  enableEnergyMarketTracking?: boolean;
  pricePerSession?: number;
  pricePerHour?: number;
  pricePerKwh?: number;
  _id?: string;
  connectorId?: number;
  components: [];
  surchargePerKwh?: number;
  currency?: string;
  id?: string;
};

export type EvseControllerConnector = {
  _id?: string;
  connectorId?: number;
  evseId?: string;
  id?: string;
  powerType?: string;
  standard?: string;
};

export enum EvseControllerConnectivityState {
  Connected = 'connected',
  MaybeConnected = 'maybe-connected',
  Disconnected = 'disconnected',
  AccessDenied = 'access-denied',
  Unknown = 'unknown',
  PendingFirstConnection = 'pending-first-connection',
}

export enum EvseControllerConnectionProtocol {
  Ws = 'ws',
  Wss = 'wss',
}

export enum EvseControllerConnectionType {
  Public = 'public',
  Private = 'private',
}

export enum EvseControllerAuthenticationMethod {
  ClientCertificates = 'client_certificates',
  Basic = 'basic',
  None = 'none',
}

type ConnectorNode = {
  connectorId?: number;
  errorCode?: string;
  status?: string;
  ocppConnectorId?: number;
  ocppEvseId?: number;
};

type AccessPolicy =
  | 'employeeReimburse'
  | 'businessReimburse'
  | 'communityReimburse'
  | 'noReimburse';

export enum OCPPProtocolVersion {
  OCPP15 = 'ocpp1.5',
  OCPP16 = 'ocpp1.6',
  OCPP201 = 'ocpp2.0.1',
}

interface EvseAuthenticationError {
  message: string;
}

type EvseAuthenticationState = 'skipped' | 'error' | 'success';

interface EvseAuthenticationAttempt {
  lastAttemptedAt?: Date;
  state?: EvseAuthenticationState;
  error?: EvseAuthenticationError;
}

export type EvseControllerConfigurationItem = {
  key: string;
  value: any;
  readonly: boolean;
  variable?: { name: string; instance?: string };
  component?: { name: string; instance?: string };
  evseId?: number;
  connectorId?: number;
};
